/**
 * ============================================================================
 *  File        : frontend/src/pages/CaseWeightAuditPage.tsx
 *  Module      : pages
 *  Purpose     : /cases/:id/weight-audit 重量稽核（S3 spec §8.2 / §D / §12.3）
 *  Author      : EF Engineering
 *  Created     : 2026-05-31
 *  Updated     : 2026-06-01
 *  License     : Proprietary - ECO FAITH Platform v3.0
 * ============================================================================
 *
 *  RBAC gate：view = marketplace.weight_variance.view（讀），
 *            create = marketplace.weight_variance.create（happy-path 推進 + 建 review），
 *            resolve = marketplace.weight_variance.resolve（PERMISSIONS.weightManualCorrect）。
 *  20kg 規則（spec §D / §12.3）+ lifecycle 推進（執行中 binary 已驗 2026-06-01）：
 *    本頁兩個表單 POST /cases/:id/weight-audits，驅動 v13 happy-path：
 *    ① 貨車總重量（variance_type=truck_total）：member/venue 卡車総重量，
 *       abs ≤ 20kg → Shipped → VenueReceived（不建 review）；
 *       > 20kg + 影響可信度 → 建 review 並進 TruckWeightVarianceReview（阻擋）。
 *    ② 単品重量表（variance_type=item_weight）：逐品 member vs venue 重量，一次提交；
 *       全部 abs ≤ 20kg → VenueReceived → WeightTablesReceived（不建 review）；
 *       任一 > 20kg → 建 review 並進 ItemWeightVarianceReview（阻擋）。
 *    後端 response（WeightVarianceEvalDto: accepted/blocked/review）+ workflow-state 為權威；
 *    前端僅以 abs(member - venue) > 20 做輸入階段提示 highlight，不自行裁定權威重量。
 */

import { useMemo, type ReactElement } from 'react'
import { useForm, useFieldArray, useWatch } from 'react-hook-form'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { getCase, type CaseItemDto } from '../api/cases'
import { getWorkflowState } from '../api/workflow'
import {
  createWeightVarianceReview,
  listWeightVarianceReviews,
  submitResolution,
  WEIGHT_VARIANCE_THRESHOLD_KG,
  type WeightVarianceEvalDto,
} from '../api/weights'
import { listHourensou, resolveHourensou, type HourensouTaskDto } from '../api/hourensou'
import { ApiError } from '../api/client'
import { useStepUp } from '../components/StepUpDialog'
import { usePermissions } from '../hooks/usePermissions'
import { PERMISSIONS } from '../lib/permissions'
import { Badge } from '../components/ui/badge'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Skeleton } from '../components/ui/skeleton'
import { cn } from '../lib/cn'
import { toast } from '../components/ui/sonner'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table'

interface PageProps {
  t: (key: string) => string
}

function errDetail(error: unknown): string {
  return error instanceof ApiError ? `（${error.message}）` : ''
}

/** abs 差異 kg（字串 Decimal → number，僅供 UI 提示；權威判定看後端 response）。 */
function absDiff(a: string, b: string): number {
  const na = Number(a)
  const nb = Number(b)
  if (!Number.isFinite(na) || !Number.isFinite(nb)) return Number.NaN
  return Math.abs(na - nb)
}

export function CaseWeightAuditPage({ t }: PageProps): ReactElement {
  const params = useParams()
  const caseId = params.id ?? ''
  const queryClient = useQueryClient()
  const { can } = usePermissions()
  const canCreate = can(PERMISSIONS.weightVarianceCreate)
  const canResolve = can(PERMISSIONS.weightManualCorrect)

  const caseQuery = useQuery({
    queryKey: ['case', caseId],
    queryFn: () => getCase(caseId),
    enabled: caseId !== '',
  })
  const workflowQuery = useQuery({
    queryKey: ['case', caseId, 'workflow-state'],
    queryFn: () => getWorkflowState(caseId),
    enabled: caseId !== '',
  })
  const reviewsQuery = useQuery({
    queryKey: ['case', caseId, 'weight-audits'],
    queryFn: () => listWeightVarianceReviews(caseId),
    enabled: caseId !== '',
  })

  // 重量裁定/解決 = 高風險端點 → 送出前先 2FA step-up（spec §9.2）。
  const resolveStepUp = useStepUp(t)
  const resolveMutation = useMutation({
    mutationFn: (vars: { reviewId: number; useVenue: boolean; code: string }) =>
      submitResolution(
        vars.reviewId,
        {
          as_role: 'venue',
          resolution_type: vars.useVenue ? 'use_venue_weight' : 'use_member_weight',
          resolved_weight_kg: null,
          note: null,
        },
        vars.code,
      ),
    onSuccess: () => {
      toast.success(t('weightAudit.resolve.success'))
      void queryClient.invalidateQueries({ queryKey: ['case', caseId, 'weight-audits'] })
      void queryClient.invalidateQueries({ queryKey: ['case', caseId, 'workflow-state'] })
    },
    onError: (error) => toast.error(`${t('weightAudit.resolve.error')}${errDetail(error)}`),
  })

  const items = useMemo<CaseItemDto[]>(() => caseQuery.data?.items ?? [], [caseQuery.data])
  const reviews = reviewsQuery.data?.data ?? []
  const workflowState = workflowQuery.data?.workflow_state ?? null

  const onAdvanced = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['case', caseId, 'workflow-state'] })
    void queryClient.invalidateQueries({ queryKey: ['case', caseId, 'weight-audits'] })
    void queryClient.invalidateQueries({ queryKey: ['case', caseId] })
  }

  return (
    <div className="space-y-4">
      {/* 目前 workflow 狀態（推進後即時更新）*/}
      <Card>
        <CardHeader>
          <CardTitle>{t('weightAudit.state.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">{t('weightAudit.state.current')}</span>
            <Badge variant="secondary" data-testid="workflow-state">
              {workflowState ?? '—'}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* ① 貨車總重量 happy-path */}
      <Card>
        <CardHeader>
          <CardTitle>{t('weightAudit.truck.title')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-muted-foreground">{t('weightAudit.truck.hint')}</p>
          {!canCreate ? (
            <Badge variant="outline">{t('rbac.forbidden')}</Badge>
          ) : (
            <TruckWeightForm t={t} caseId={caseId} onAdvanced={onAdvanced} />
          )}
        </CardContent>
      </Card>

      {/* ② 単品重量表 happy-path */}
      <Card>
        <CardHeader>
          <CardTitle>{t('weightAudit.item.title')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-muted-foreground">{t('weightAudit.item.hint')}</p>
          {!canCreate ? (
            <Badge variant="outline">{t('rbac.forbidden')}</Badge>
          ) : items.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('weightAudit.item.noItems')}</p>
          ) : (
            <ItemWeightForm t={t} caseId={caseId} items={items} onAdvanced={onAdvanced} />
          )}
        </CardContent>
      </Card>

      {/* 既建立的重量差異 review（> 20kg 才會有）*/}
      <Card>
        <CardHeader>
          <CardTitle>
            {t('weightAudit.reviews.title')}（{reviews.length}）
          </CardTitle>
        </CardHeader>
        <CardContent>
          {reviewsQuery.isPending ? (
            <Skeleton className="h-24 w-full" />
          ) : reviews.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('weightAudit.reviews.empty')}</p>
          ) : (
            <div className="overflow-x-auto rounded-md border border-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('weightAudit.col.varianceType')}</TableHead>
                    <TableHead className="text-right">{t('weightAudit.col.variance')}</TableHead>
                    <TableHead className="text-right">{t('weightAudit.col.threshold')}</TableHead>
                    <TableHead>{t('weightAudit.col.status')}</TableHead>
                    <TableHead>{t('weightAudit.col.resolution')}</TableHead>
                    <TableHead className="text-right">{t('weightAudit.col.action')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reviews.map((review) => (
                    <TableRow key={review.id}>
                      <TableCell>{review.variance_type}</TableCell>
                      <TableCell className="text-right">{review.variance_kg}</TableCell>
                      <TableCell className="text-right">{review.threshold_kg}</TableCell>
                      <TableCell>
                        <Badge variant="secondary">{review.status}</Badge>
                      </TableCell>
                      <TableCell>{review.resolution_type ?? '—'}</TableCell>
                      <TableCell className="text-right">
                        {canResolve && !review.resolution_type ? (
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            data-testid={`weight-resolve-${review.id}`}
                            disabled={resolveMutation.isPending}
                            onClick={() =>
                              resolveStepUp.request((code) =>
                                resolveMutation.mutate({
                                  reviewId: review.id,
                                  useVenue: true,
                                  code,
                                }),
                              )
                            }
                          >
                            {t('weightAudit.resolve.useVenue')}
                          </Button>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* 報連相（hourensou）：20kg 差異 / P+Q+R≠K / 工程逾期等需確認事項（spec §10 / §D5）。*/}
      <HourensouPanel t={t} caseId={caseId} />

      {resolveStepUp.dialog}
    </div>
  )
}

// ---- 報連相 panel（spec §10 / §D5）-----------------------------------------

function HourensouPanel({ t, caseId }: { t: (key: string) => string; caseId: string }): ReactElement {
  const queryClient = useQueryClient()
  const { can } = usePermissions()
  const canView = can(PERMISSIONS.hourensouView)
  const canResolve = can(PERMISSIONS.hourensouResolve)

  const query = useQuery({
    queryKey: ['case', caseId, 'hourensou'],
    queryFn: () => listHourensou(caseId),
    enabled: caseId !== '' && canView,
  })

  const resolveMutation = useMutation({
    mutationFn: (taskId: number) => resolveHourensou(taskId, { resolution_note: null }),
    onSuccess: () => {
      toast.success(t('hourensou.resolve.success'))
      void queryClient.invalidateQueries({ queryKey: ['case', caseId, 'hourensou'] })
    },
    onError: (error) => toast.error(`${t('hourensou.resolve.error')}${errDetail(error)}`),
  })

  const tasks: HourensouTaskDto[] = query.data?.data ?? []

  const typeLabel = (issueType: string): string => {
    if (issueType === 'field') return t('hourensou.type.field')
    if (issueType === 'numeric') return t('hourensou.type.numeric')
    if (issueType === 'process') return t('hourensou.type.process')
    return issueType
  }
  const statusLabel = (status: string): string => {
    if (status === 'open') return t('hourensou.status.open')
    if (status === 'acknowledged') return t('hourensou.status.acknowledged')
    if (status === 'resolved') return t('hourensou.status.resolved')
    return status
  }

  if (!canView) {
    return (
      <Card data-testid="hourensou-panel">
        <CardHeader>
          <CardTitle>{t('hourensou.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <Badge variant="outline">{t('rbac.forbidden')}</Badge>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card data-testid="hourensou-panel">
      <CardHeader>
        <CardTitle>
          {t('hourensou.title')}（{tasks.length}）
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground">{t('hourensou.subtitle')}</p>
        {query.isPending ? (
          <Skeleton className="h-24 w-full" />
        ) : query.isError ? (
          <p className="text-sm text-destructive">{t('hourensou.loadError')}</p>
        ) : tasks.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('hourensou.empty')}</p>
        ) : (
          <div className="overflow-x-auto rounded-md border border-border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('hourensou.col.type')}</TableHead>
                  <TableHead>{t('hourensou.col.category')}</TableHead>
                  <TableHead>{t('hourensou.col.assignee')}</TableHead>
                  <TableHead>{t('hourensou.col.message')}</TableHead>
                  <TableHead>{t('hourensou.col.status')}</TableHead>
                  <TableHead className="text-right">{t('hourensou.col.action')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tasks.map((task) => (
                  <TableRow key={task.id} data-testid={`hourensou-row-${task.id}`}>
                    <TableCell>
                      <Badge variant="secondary">{typeLabel(task.issue_type)}</Badge>
                    </TableCell>
                    <TableCell>{task.category_label}</TableCell>
                    <TableCell>{task.assigned_role}</TableCell>
                    <TableCell className="max-w-[20rem] whitespace-pre-wrap text-sm">
                      {task.message}
                    </TableCell>
                    <TableCell>
                      <Badge variant={task.status === 'resolved' ? 'outline' : 'secondary'}>
                        {statusLabel(task.status)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {canResolve && task.status !== 'resolved' ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          data-testid={`hourensou-resolve-${task.id}`}
                          disabled={resolveMutation.isPending}
                          onClick={() => resolveMutation.mutate(task.id)}
                        >
                          {t('hourensou.resolve.button')}
                        </Button>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// ---- ① 貨車總重量表單 ------------------------------------------------------

function TruckWeightForm({
  t,
  caseId,
  onAdvanced,
}: {
  t: (key: string) => string
  caseId: string
  onAdvanced: () => void
}): ReactElement {
  const form = useForm<{ memberWeight: string; venueWeight: string }>({
    defaultValues: { memberWeight: '', venueWeight: '' },
  })
  const memberWeight = useWatch({ control: form.control, name: 'memberWeight' }) ?? ''
  const venueWeight = useWatch({ control: form.control, name: 'venueWeight' }) ?? ''
  const diff = absDiff(memberWeight, venueWeight)
  const over = Number.isFinite(diff) && diff > WEIGHT_VARIANCE_THRESHOLD_KG

  const mutation = useMutation({
    mutationFn: (v: { memberWeight: string; venueWeight: string }) =>
      createWeightVarianceReview(caseId, {
        variance_type: 'truck_total',
        member_weight_kg: v.memberWeight.trim(),
        venue_weight_kg: v.venueWeight.trim(),
        // > 20kg 時：影響重量可信度 → 進 blocking review（業務上現場需三方確認）。
        affects_truck_credibility: over,
        field_issue: false,
        affects_amount: false,
      }),
    onSuccess: (result: WeightVarianceEvalDto) => {
      if (result.blocked) {
        toast.warning(t('weightAudit.truck.blocked'))
      } else {
        toast.success(t('weightAudit.truck.accepted'))
      }
      onAdvanced()
    },
    onError: (error) => toast.error(`${t('weightAudit.truck.error')}${errDetail(error)}`),
  })

  const submit = form.handleSubmit((v) => mutation.mutate(v))

  return (
    <form className="space-y-3" onSubmit={(e) => void submit(e)}>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <label className="space-y-1 text-sm">
          <span className="text-muted-foreground">{t('weightAudit.truck.memberTotal')}</span>
          <Input
            inputMode="decimal"
            data-testid="truck-member-weight"
            aria-label={t('weightAudit.truck.memberTotal')}
            {...form.register('memberWeight', { required: true })}
          />
        </label>
        <label className="space-y-1 text-sm">
          <span className="text-muted-foreground">{t('weightAudit.truck.venueTotal')}</span>
          <Input
            inputMode="decimal"
            data-testid="truck-venue-weight"
            aria-label={t('weightAudit.truck.venueTotal')}
            {...form.register('venueWeight', { required: true })}
          />
        </label>
        <div className="flex items-end">
          <div
            className={cn(
              'rounded-md border px-3 py-2 text-sm',
              over ? 'border-destructive text-destructive' : 'border-border text-muted-foreground',
            )}
          >
            {t('weightAudit.col.variance')}:{' '}
            {Number.isFinite(diff) ? `${diff} kg` : '—'}
            {over ? `（> ${WEIGHT_VARIANCE_THRESHOLD_KG}kg）` : ''}
          </div>
        </div>
      </div>
      <Button
        type="submit"
        data-testid="truck-weight-submit"
        disabled={mutation.isPending || !memberWeight.trim() || !venueWeight.trim()}
      >
        {mutation.isPending ? t('weightAudit.truck.submitting') : t('weightAudit.truck.submit')}
      </Button>
    </form>
  )
}

// ---- ② 単品重量表（逐品 member vs venue，一次提交）-------------------------

function ItemWeightForm({
  t,
  caseId,
  items,
  onAdvanced,
}: {
  t: (key: string) => string
  caseId: string
  items: CaseItemDto[]
  onAdvanced: () => void
}): ReactElement {
  const form = useForm<{
    rows: { itemId: number; itemName: string; lineNo: number; memberWeight: string; venueWeight: string }[]
  }>({
    defaultValues: {
      rows: items.map((it) => ({
        itemId: it.id,
        itemName: it.item_name,
        lineNo: it.line_no,
        memberWeight: it.estimated_weight_kg ?? '',
        venueWeight: it.estimated_weight_kg ?? '',
      })),
    },
  })
  const { fields } = useFieldArray({ control: form.control, name: 'rows' })
  const watched = useWatch({ control: form.control, name: 'rows' }) ?? []

  // 逐品提交（後端每列一筆 weight-audits；任一 > 20kg 該列 affects_amount→ blocking review）。
  const mutation = useMutation({
    mutationFn: async (rows: typeof watched) => {
      let lastEval: WeightVarianceEvalDto | null = null
      for (const row of rows) {
        const over = absDiff(row.memberWeight, row.venueWeight) > WEIGHT_VARIANCE_THRESHOLD_KG
        lastEval = await createWeightVarianceReview(caseId, {
          variance_type: 'item_weight',
          item_id: row.itemId,
          member_weight_kg: row.memberWeight.trim(),
          venue_weight_kg: row.venueWeight.trim(),
          affects_truck_credibility: false,
          field_issue: over,
          affects_amount: over,
        })
      }
      return lastEval
    },
    onSuccess: (result) => {
      if (result?.blocked) {
        toast.warning(t('weightAudit.item.blocked'))
      } else {
        toast.success(t('weightAudit.item.accepted'))
      }
      onAdvanced()
    },
    onError: (error) => toast.error(`${t('weightAudit.item.error')}${errDetail(error)}`),
  })

  const submit = form.handleSubmit((v) => mutation.mutate(v.rows))

  const allFilled = watched.every(
    (r) => (r.memberWeight ?? '').trim() !== '' && (r.venueWeight ?? '').trim() !== '',
  )

  return (
    <form className="space-y-3" onSubmit={(e) => void submit(e)}>
      <div className="overflow-x-auto rounded-md border border-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('field.itemName')}</TableHead>
              <TableHead className="min-w-[8rem]">{t('weightAudit.col.memberWeight')}</TableHead>
              <TableHead className="min-w-[8rem]">{t('weightAudit.col.venueWeight')}</TableHead>
              <TableHead className="text-right">{t('weightAudit.col.variance')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {fields.map((field, index) => {
              const row = watched[index]
              const diff = row ? absDiff(row.memberWeight, row.venueWeight) : Number.NaN
              const over = Number.isFinite(diff) && diff > WEIGHT_VARIANCE_THRESHOLD_KG
              return (
                <TableRow key={field.id} className={cn(over && 'bg-destructive/10')}>
                  <TableCell className="font-medium">
                    #{field.lineNo} {field.itemName}
                  </TableCell>
                  <TableCell>
                    <Input
                      inputMode="decimal"
                      data-testid={`item-member-weight-${field.itemId}`}
                      aria-label={t('weightAudit.col.memberWeight')}
                      {...form.register(`rows.${index}.memberWeight`)}
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      inputMode="decimal"
                      data-testid={`item-venue-weight-${field.itemId}`}
                      aria-label={t('weightAudit.col.venueWeight')}
                      {...form.register(`rows.${index}.venueWeight`)}
                    />
                  </TableCell>
                  <TableCell
                    className={cn('text-right', over && 'font-semibold text-destructive')}
                  >
                    {Number.isFinite(diff) ? `${diff}` : '—'}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>
      <Button
        type="submit"
        data-testid="item-weight-submit"
        disabled={mutation.isPending || !allFilled}
      >
        {mutation.isPending ? t('weightAudit.item.submitting') : t('weightAudit.item.submit')}
      </Button>
    </form>
  )
}
